/**
 * Custom Handle Component
 * Input = right-pointing arrow | Output = filled circle dot
 * Colors: white (idle) → green (node selected) → white (hovered)
 */

import React, { useState } from 'react';
import { Handle, Position, Connection } from 'reactflow';
import { ProcessElementPort } from '@flow-studio/types';
import { useUIStore } from '@flow-studio/store';
import { useWorkflowStore } from '@flow-studio/store';
import { NodeSearchModal } from '../Modals/NodeSearchModal';

interface CustomHandleProps {
  port: ProcessElementPort;
  nodeId: string;
}

const positionMap: Record<string, Position> = {
  top: Position.Top,
  right: Position.Right,
  bottom: Position.Bottom,
  left: Position.Left,
};

export const CustomHandle: React.FC<CustomHandleProps> = ({ port, nodeId }) => {
  const { setSelectedHandle, selectedNodes } = useUIStore();
  const { edges } = useWorkflowStore();
  const position = positionMap[port.position] || Position.Right;

  const [showNodeSearch, setShowNodeSearch] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isAddBtnHovered, setIsAddBtnHovered] = useState(false);

  const isInput  = port.portType === 'input';
  const isOutput = port.portType === 'output';
  const isConnected   = isOutput && edges.some(e => e.source === nodeId && e.sourceHandle === port.portKey);
  const isNodeSelected = selectedNodes.includes(nodeId);

  const isError = !!port.isErrorPort;

  // Error ports are always red; other ports follow idle/selected/hovered logic
  const color = isError
    ? (isHovered ? '#ff6b6b' : '#ef4444')
    : isHovered
    ? '#ffffff'
    : isNodeSelected
    ? '#309B48'
    : 'rgba(255, 255, 255, 0.55)';

  const glow = isError
    ? '0 0 6px rgba(239, 68, 68, 0.7)'
    : isNodeSelected
    ? '0 0 8px rgba(48, 155, 72, 0.7)'
    : isHovered
    ? '0 0 6px rgba(255, 255, 255, 0.5)'
    : 'none';

  // Shape: use stored handleStyle (error ports default to triangle if not explicitly set)
  const effectiveShape: string = port.handleStyle || (isError ? 'triangle' : (isInput ? 'arrow' : 'circle'));

  const pos = port.position || (isInput ? 'left' : 'right');

  // Offsets to push a shape out from the node border
  // left/right incorporate port.offsetX for fine-grained horizontal nudging
  const _leftHorizShift  = 3 - (port.offsetX || 0);
  const _rightHorizShift = 3 + (port.offsetX || 0);
  const circleOffsets: Record<string, string> = {
    right:  `translate(calc(50% + ${_rightHorizShift}px), -50%)`,
    left:   `translate(calc(-50% - ${_leftHorizShift}px), -50%)`,
    bottom: 'translate(-50%, calc(50% + 3px))',
    top:    'translate(-50%, calc(-50% - 3px))',
  };
  const outerOffsets: Record<string, string> = {
    right:  'translate(calc(100% + 5px), -50%)',
    left:   'translate(calc(-100% - 5px), -50%)',
    bottom: 'translate(-50%, calc(100% + 5px))',
    top:    'translate(-50%, calc(-100% - 5px))',
  };

  // Per-position clip-paths for shapes that need direction awareness
  const triangleClips: Record<string, string> = {
    right:  'polygon(0 0, 100% 50%, 0 100%)',
    left:   'polygon(100% 0, 0 50%, 100% 100%)',
    bottom: 'polygon(0 0, 100% 0, 50% 100%)',
    top:    'polygon(50% 0, 0 100%, 100% 100%)',
  };

  const shapeStyles: React.CSSProperties = (() => {
    switch (effectiveShape) {
      case 'arrow': {
        const arrowRotations: Record<string, string> = {
          right:  isInput ? 'rotate(180deg)' : 'rotate(0deg)',
          left:   isInput ? 'rotate(0deg)'   : 'rotate(180deg)',
          bottom: isInput ? 'rotate(270deg)' : 'rotate(90deg)',
          // 'component' port (top input) always points up ↑; other top inputs point down
          top:    port.portKey === 'component' ? 'rotate(270deg)' : (isInput ? 'rotate(90deg)' : 'rotate(270deg)'),
        };
        const arrowOffsets: Record<string, string> = {
          right:  isInput ? 'translate(0, -50%)'      : outerOffsets.right,
          left:   isInput ? 'translate(-100%, -50%)'  : outerOffsets.left,
          bottom: isInput ? 'translate(-50%, 0)'      : outerOffsets.bottom,
          top:    isInput ? 'translate(-50%, -100%)'  : outerOffsets.top,
        };
        return {
          width: '14px', height: '10px',
          borderRadius: 0, border: 'none',
          clipPath: 'polygon(0 0, 65% 0, 100% 50%, 65% 100%, 0 100%)',
          transform: `${arrowOffsets[pos]} ${arrowRotations[pos]}`,
        };
      }
      case 'triangle':
        return {
          width: '12px', height: '12px',
          borderRadius: 0, border: 'none',
          clipPath: triangleClips[pos] || triangleClips.right,
          transform: circleOffsets[pos],
        };
      case 'square':
        return {
          width: '10px', height: '10px',
          borderRadius: '2px',
          border: `1.5px solid rgba(255, 255, 255, 0.15)`,
          transform: circleOffsets[pos],
        };
      case 'diamond':
        return {
          width: '10px', height: '10px',
          borderRadius: '2px',
          border: `1.5px solid rgba(255, 255, 255, 0.15)`,
          transform: `${circleOffsets[pos]} rotate(45deg)`,
        };
      case 'circle':
      default:
        return {
          width: '10px', height: '10px',
          borderRadius: '50%',
          border: `1.5px solid rgba(255, 255, 255, 0.15)`,
          transform: circleOffsets[pos],
        };
    }
  })();

  // Connection guard for system component ports:
  //  • my-components (source) may only connect to a 'component' target handle
  //  • component (target) may only accept connections from a 'my-components' source handle
  const isValidConnection = port.portKey === 'my-components'
    ? (c: Connection) => c.targetHandle === 'component'
    : port.portKey === 'component'
      ? (c: Connection) => c.sourceHandle === 'my-components'
      : undefined;

  // Show label for every port that has one — including isMainPort ports on multi-output nodes
  const showLabel = !!port.label;
  const portLabelIcons: Record<string, string> = { 'my-components': '◈ ', 'component': '▴ ' };
  const labelText = (portLabelIcons[port.portKey] ?? '') + (port.label ?? '');
  const oy = port.offsetY || 0;
  const ox = port.offsetX || 0;
  const lox = port.labelOffsetX ?? 0;
  const loy = port.labelOffsetY ?? 0;

  // Offset along the node edge so multiple handles on the same side don't stack
  const positionOffset: React.CSSProperties =
    pos === 'left'  || pos === 'right'  ? { top:  oy !== 0 ? `calc(50% + ${oy}px)` : undefined } :
    pos === 'top'   || pos === 'bottom' ? { left: ox !== 0 ? `calc(50% + ${ox}px)` : undefined } :
    {};

  const handleStyle: React.CSSProperties = {
    background:  color,
    boxShadow:   glow,
    transition:  'background 0.15s, box-shadow 0.15s',
    cursor:      'crosshair',
    ...shapeStyles,
    ...positionOffset,
  };

  // Labels sit OUTSIDE the node body, next to the handle — never overlapping node content
  // Uses `pos` (the effective position with default fallback) rather than `port.position`
  // because port.position can be undefined for ports that rely on the input/output default.
  const labelStyle: React.CSSProperties = {
    position:      'absolute',
    pointerEvents: 'none',
    userSelect:    'none',
    fontSize:      '10px',
    fontWeight:    500,
    color:         'rgba(255,255,255,0.7)',
    whiteSpace:    'nowrap',
    background:    'rgba(2,6,23,0.75)',
    padding:       '2px 6px',
    borderRadius:  '3px',
    zIndex:        5,
    ...(pos === 'right'  && { left: `calc(100% + ${18 + lox}px)`, top: `calc(50% + ${oy + loy}px)`, transform: 'translateY(-50%)' }),
    ...(pos === 'left'   && { right: `calc(100% + ${18 - lox}px)`, top: `calc(50% + ${oy + loy}px)`, transform: 'translateY(-50%)' }),
    ...(pos === 'top'    && { top: `${-24 + loy}px`, left: `calc(50% + ${ox + lox}px)`, transform: 'translateX(-50%)' }),
    ...(pos === 'bottom' && { bottom: `${-24 - loy}px`, left: `calc(50% + ${ox + lox}px)`, transform: 'translateX(-50%)' }),
  };

  // Add-node button — positioned above the output circle
  const addBtnStyle: React.CSSProperties = {
    position:     'absolute',
    width:        '18px',
    height:       '18px',
    borderRadius: '50%',
    background:   isAddBtnHovered ? '#16a34a' : '#2d3748',
    border:       isAddBtnHovered ? '1.5px solid #22c55e' : '1.5px solid rgba(255,255,255,0.25)',
    color:        '#ffffff',
    fontSize:     '14px',
    display:      'flex',
    alignItems:   'center',
    justifyContent: 'center',
    cursor:       'pointer',
    zIndex:       20,
    opacity:      isHovered ? 1 : 0,
    transition:   'opacity 0.15s, background 0.15s, border-color 0.15s',
    ...(pos === 'right'  && { left: 'calc(100% + 2px)',  top: `calc(50% + ${oy}px - 20px)`, transform: 'none' }),
    ...(pos === 'left'   && { right: 'calc(100% + 2px)', top: `calc(50% + ${oy}px - 20px)`, transform: 'none' }),
    ...(pos === 'bottom' && { top: 'calc(100% + 2px)',   left: `calc(50% + ${ox}px - 20px)`, transform: 'none' }),
    ...(pos === 'top'    && { bottom: 'calc(100% + 2px)', left: `calc(50% + ${ox}px - 20px)`, transform: 'none' }),
  };

  return (
    <>
      <Handle
        type={isInput ? 'target' : 'source'}
        position={position}
        id={port.portKey}
        style={handleStyle}
        title={port.label || port.portKey}
        isConnectable
        isValidConnection={isValidConnection}
        onClick={e => { e.stopPropagation(); setSelectedHandle({ nodeId, handleId: port.portKey, handleType: isInput ? 'target' : 'source' }); }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      />

      {showLabel && <span style={labelStyle}>{labelText}</span>}

      {/* Show '+' for output ports: always for isMultiple (e.g. my-components), only when empty otherwise */}
      {isOutput && (!isConnected || port.isMultiple) && (
        <button
          className="nodrag nopan"
          style={addBtnStyle}
          onClick={e => { e.stopPropagation(); setShowNodeSearch(true); }}
          onMouseDown={e => e.stopPropagation()}
          onMouseEnter={() => { setIsHovered(true); setIsAddBtnHovered(true); }}
          onMouseLeave={() => { setIsHovered(false); setIsAddBtnHovered(false); }}
          title="Add node"
        >
          +
        </button>
      )}

      {showNodeSearch && (
        <NodeSearchModal
          sourceNodeId={nodeId}
          sourceHandleId={port.portKey}
          onClose={() => setShowNodeSearch(false)}
        />
      )}
    </>
  );
};
